/**
 * 시스템 설정 — 「보증금 단계」 탭(EA-60)·단계 줄 더하기(EA-61)·은퇴(EA-62).
 *
 * 요구: R-XAD-01(`~/dev1/devnet-docs/_data/2026-09-29/planning-answers/05_확정요구사항.md` 2-5).
 * 근거: 원문 2(50명 100,000 · 100명 200,000 · 150명 300,000 · 200명 400,000 USDE, '거래소 관리자에서
 * DB테이블로 관리'), 확인 30(Q-32 — 관리자가 줄을 더함), 확인 31(마스터가 고른 단계는 그 마스터에
 * 남는다 — 표를 바꿔도 기존 마스터는 옛 조건).
 *
 * 계약: api-XB XM-93(목록, `settings.view`)·XM-94(줄 더하기, `settings.edit`)·XM-95(은퇴, `settings.edit`).
 *
 * ⚠ **돈 기준이다.** 설정 금액은 새 신청·단계 올리기의 보증금이다. 금액은 서버가 주는 내부 단위
 * 문자열 그대로 보이고, 입력은 USDE 글을 문자열로 내부 단위로 바꿔 보낸다(float 없음 —
 * `parseUsdeInput`, M-08). 「보낼 값」 줄에 서버로 가는 값을 그대로 보인다.
 *
 * ⚠ **줄을 고치거나 지우는 단추는 없다.** 계약이 더하기·은퇴만 준다 — 고른 마스터의 조건이
 * 바뀌지 않게(확인 31). 금액을 바꾸려면 새 줄을 더하고 옛 줄을 은퇴시킨다.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createPartnerTier, listPartnerTiers, retirePartnerTier } from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { AdminRef, Badge, Button, Maybe, Notice, NUM, Row, TD, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { formatUsde, parseUsdeInput, usde } from '@/format'
import type { PartnerTier } from '@/api/types'

/** 지금 쓰는 줄을 정원 오름차순으로 먼저, 은퇴한 줄은 뒤에(은퇴 새것부터). */
export function orderTiers(rows: readonly PartnerTier[]): PartnerTier[] {
  const active = rows.filter((t) => t.active).sort((a, b) => a.members - b.members || a.tierId - b.tierId)
  const retired = rows.filter((t) => !t.active).sort((a, b) => (b.retiredAt ?? 0) - (a.retiredAt ?? 0) || b.tierId - a.tierId)
  return [...active, ...retired]
}

/**
 * 입력 → 보낼 값. 정원은 1 이상 정수, 금액은 USDE(소수 둘째 자리까지) → 내부 단위 문자열(0 보다 큼).
 * 계약 XM-94 `members int > 0`, `deposit "units" > 0`. 막히면 `error` 에 까닭.
 */
export function toTierInput(membersText: string, depositText: string):
  { members: number | null; deposit: string | null; error: string | null } {
  const m = membersText.trim()
  const members = /^\d{1,9}$/.test(m) && Number(m) > 0 ? Number(m) : null
  const units = parseUsdeInput(depositText.trim())
  const deposit = units !== null && units.length <= 18 && /[1-9]/.test(units) ? units : null
  if (m !== '' && members === null) return { members, deposit, error: 'The capacity is a whole number of members, 1 or more.' }
  if (depositText.trim() !== '' && deposit === null) {
    return { members, deposit, error: 'The set amount is a USDE amount greater than 0 (up to two decimal places).' }
  }
  return { members, deposit, error: null }
}

/** EA-60 — 보증금 단계 탭(XM-93). */
export function TiersTab() {
  const { has } = useSession()
  const q = useQuery({ queryKey: ['partner-tiers'], queryFn: listPartnerTiers })
  const [adding, setAdding] = useState(false)
  const [retiring, setRetiring] = useState<PartnerTier | null>(null)
  const rows = orderTiers(q.data?.tiers ?? [])
  const canEdit = has('settings.edit')

  return (
    <>
      <p className="mb-4 text-xs text-slate-500">
        The capacity and collateral tiers a master chooses in the partner application. Changing the table does not
        change masters who already chose a tier; new applications and tier upgrades use the current table.
      </p>
      {canEdit && (
        <div className="mb-4"><Button onClick={() => setAdding(true)}>Add tier row</Button></div>
      )}
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="There are no tier rows.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Capacity</th><th className={TH}>Set amount</th>
            <th className={TH}>Status</th><th className={TH}>Masters using</th><th className={TH}>Created at</th>
            <th className={TH}>Created by</th><th className={TH}>Retired at</th><th className={TH}>Retired by</th>
            {canEdit && <th className={TH}>Actions</th>}
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.tierId} className={t.active ? '' : 'text-slate-400'}>
                <td className={TD}>{t.tierId}</td>
                <td className={TD}>{t.members.toLocaleString('en-US')} members</td>
                <td className={NUM}>{usde(t.deposit)}</td>
                <td className={TD}>{t.active ? <Badge tone="ok">In use</Badge> : 'Retired'}</td>
                <td className={TD}><Maybe value={t.mastersUsing} unit=" members" /></td>
                <td className={TD}><Time ms={t.createdAt} /></td>
                <td className={TD}><AdminRef adminId={t.createdBy} empty="System" /></td>
                <td className={TD}><Time ms={t.retiredAt} /></td>
                <td className={TD}><AdminRef adminId={t.retiredBy} /></td>
                {canEdit && (
                  <td className={TD}>
                    {t.active && <Button tone="plain" onClick={() => setRetiring(t)}>Retire</Button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {adding && <AddTierDialog onClose={() => setAdding(false)} />}
      {retiring && <RetireTierDialog tier={retiring} onClose={() => setRetiring(null)} />}
    </>
  )
}

/** EA-61 — 단계 줄 더하기(XM-94). */
function AddTierDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const [membersText, setMembersText] = useState('')
  const [depositText, setDepositText] = useState('')
  const { members, deposit, error } = toTierInput(membersText, depositText)
  const ready = members !== null && deposit !== null

  async function submit() {
    if (members === null || deposit === null) return
    const send = async () => {
      const t = await createPartnerTier(members, deposit)
      setFlash({
        tone: 'ok',
        text: `Added tier row #${t.tierId} — ${t.members.toLocaleString('en-US')} members, ${formatUsde(t.deposit) ?? t.deposit} USDE.`,
      })
      await qc.invalidateQueries({ queryKey: ['partner-tiers'] })
    }
    await submitWrite(w, 'Add tier row', send, onClose)
  }

  return (
    <Confirm open title="Add tier row" action="Add" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={!ready}>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Capacity (members)</span>
          <input value={membersText} inputMode="numeric" aria-label="Capacity"
            onChange={(e) => setMembersText(e.target.value)}
            className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Set amount (USDE)</span>
          <span className="flex items-center gap-2">
            <input value={depositText} inputMode="decimal" aria-label="Set amount"
              onChange={(e) => setDepositText(e.target.value)}
              className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800" />
            <span>USDE</span>
          </span>
        </label>
        {error && <p className="text-xs text-red-700 dark:text-red-400">{error}</p>}
        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <Row label="Value to send">
            <code className="break-all font-mono text-xs">
              {ready ? JSON.stringify({ members, deposit }) : '—'}
            </code>
          </Row>
        </dl>
        <p className="text-xs text-slate-500">
          New applications and tier upgrades use this row. Masters who already chose a tier keep it. Rows cannot be
          edited — to change one, add a new row and retire the old one.
        </p>
      </div>
    </Confirm>
  )
}

/** EA-62 — 단계 줄 은퇴(XM-95). */
function RetireTierDialog({ tier, onClose }: { tier: PartnerTier; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      await retirePartnerTier(tier.tierId)
      setFlash({ tone: 'ok', text: `Retired tier row #${tier.tierId}.` })
      await qc.invalidateQueries({ queryKey: ['partner-tiers'] })
    }
    await submitWrite(w, 'Retire tier row', send, onClose)
  }

  return (
    <Confirm open title={`Retire tier row #${tier.tierId}`} action="Retire" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
        <Row label="Capacity">{tier.members.toLocaleString('en-US')} members</Row>
        <Row label="Set amount"><span className="font-mono">{usde(tier.deposit)}</span></Row>
        <Row label="Masters using"><Maybe value={tier.mastersUsing} unit=" members" /></Row>
      </dl>
      <div className="mt-3">
        <Notice tone="warn">
          Once retired, new applications and tier upgrades do not use this row. Masters who chose this row keep
          their tier.
        </Notice>
      </div>
    </Confirm>
  )
}
